// src/utils/HybridBrowserProvider.js
// 
// HybridBrowserProvider: Routes read-only RPC calls through a stable public RPC,
// while signing/wallet calls go through the WalletConnect relay (wallet.provider).
//
// This prevents transaction failures when WalletConnect's session-based RPC
// (bsc.twnodes.com/naas/session/...) expires after inactivity.
//
// Architecture:
//   READ calls (eth_blockNumber, eth_getBalance, etc.) → Public BSC RPC
//   WALLET calls (eth_sendTransaction, personal_sign, etc.) → WalletConnect relay → User's wallet

import { ethers } from 'ethers';

const BSC_PUBLIC_RPC = 'https://bsc-dataseed.binance.org/';

// Methods that MUST go through the wallet provider (signing, accounts, chain management)
const WALLET_METHODS = new Set([
  'eth_sendTransaction',
  'eth_signTransaction',
  'eth_sign',
  'personal_sign',
  'eth_signTypedData',
  'eth_signTypedData_v3',
  'eth_signTypedData_v4',
  'eth_accounts',
  'eth_requestAccounts',
  'eth_chainId',
  'net_version',
  'wallet_switchEthereumChain',
  'wallet_addEthereumChain',
  'wallet_requestPermissions',
  'wallet_watchAsset',
]);

export class HybridBrowserProvider extends ethers.BrowserProvider {
  #publicProvider;

  constructor(walletProvider, network) {
    super(walletProvider, network);
    this.#publicProvider = new ethers.JsonRpcProvider(BSC_PUBLIC_RPC);
  }

  async send(method, params) {
    if (WALLET_METHODS.has(method)) {
      // Wallet/signing calls → go through WalletConnect relay to user's wallet
      return super.send(method, params);
    }

    // Read-only calls → use stable public RPC (never expires)
    try {
      return await this.#publicProvider.send(method, params);
    } catch (publicErr) {
      // If public RPC fails, try wallet provider as fallback
      console.warn(`[HybridProvider] Public RPC failed for ${method}, trying wallet:`, publicErr.message);
      return super.send(method, params);
    }
  }
}

export default HybridBrowserProvider;
